import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';
import type { DraftAttachment } from '@ctrl-enter/types';
import type { AskExpertInput } from '@ctrl-enter/types/contracts/service-gateway';
import * as React from 'react';
import { useReducer } from 'react';
import { useAskExpert, useFileUpload } from '../../../api/gateway-provider';
import { useGenerateTitleMutation } from '../../../hooks/use-gateway-queries';
import { useNetworkConnectivity } from '../../../hooks/use-network';
import { useS3ChatIntegration } from '../../../hooks/use-s3-chat-integration';
import { useActiveMode, useUserSettings } from '../../../stores/personalization-store';
import {
  useCurrentSession,
  useS3ChatStore,
  useSessionList as useS3SessionList,
} from '../../../stores/s3-chat-store';
import { useAiSkillStore } from '../../../stores/ai-skill-store';
import { withRetry } from '../../../utils/network-retry';

const logger = createLogger('client:hook:use-chat-view-state');

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

enum ChatViewAction {
  NEW_CHAT_STARTED = 'NEW_CHAT_STARTED',
  CHAT_CLOSED = 'CHAT_CLOSED',
  OPEN_CHAT_SESSION = 'OPEN_CHAT_SESSION',
  TOGGLE_SESSION_PANEL = 'TOGGLE_SESSION_PANEL',
  DRAFT_UPDATE_SCREENSHOTS = 'DRAFT_UPDATE_SCREENSHOTS',
  DRAFT_UPDATE_FILES = 'DRAFT_UPDATE_FILES',
  DRAFT_REMOVE_SCREENSHOT = 'DRAFT_REMOVE_SCREENSHOT',
  DRAFT_REMOVE_FILE = 'DRAFT_REMOVE_FILE',
  DRAFT_UPDATE_MESSAGE = 'DRAFT_UPDATE_MESSAGE',
  DRAFT_FILE_PROCESSING_START = 'DRAFT_FILE_PROCESSING_START',
  DRAFT_FILE_PROCESSING_SUCCESS = 'DRAFT_FILE_PROCESSING_SUCCESS',
  DRAFT_FILE_PROCESSING_ERROR = 'DRAFT_FILE_PROCESSING_ERROR',
  DRAFT_SET_PAGE_CONTEXT = 'DRAFT_SET_PAGE_CONTEXT',
  DRAFT_CLEAR_PAGE_CONTEXT = 'DRAFT_CLEAR_PAGE_CONTEXT',
  SUBMIT_MESSAGE = 'SUBMIT_MESSAGE',
  SET_ERROR = 'SET_ERROR',
  SUBMIT_MESSAGE_RESOLVED = 'SUBMIT_MESSAGE_RESOLVED',
}

export type PageContextDraft = {
  content: string;
  title: string;
  url: string;
  charCount: number;
  wasTruncated: boolean;
};

type ChatViewState = {
  draftUserMessage: string;
  draftScreenshots: DraftAttachment[];
  draftFiles: DraftAttachment[];
  draftPageContext: PageContextDraft | null;
  sessionId: string;
  sessionPanelVisible: boolean;
  isLoading: boolean;
  error: string | null;
  targetMessageId: string | null; // For scrolling to specific message
};

const initialState: ChatViewState = {
  draftUserMessage: '',
  sessionId: '',
  draftScreenshots: [],
  draftFiles: [],
  draftPageContext: null,
  sessionPanelVisible: false,
  isLoading: false,
  error: null,
  targetMessageId: null,
};

type ChatViewActionType =
  | {
      type: ChatViewAction.CHAT_CLOSED;
    }
  | { type: ChatViewAction.NEW_CHAT_STARTED }
  | { type: ChatViewAction.OPEN_CHAT_SESSION; payload: { sessionId: string; messageId?: string } }
  | { type: ChatViewAction.TOGGLE_SESSION_PANEL }
  | { type: ChatViewAction.DRAFT_UPDATE_SCREENSHOTS; payload: { screenshots: DraftAttachment[] } }
  | { type: ChatViewAction.DRAFT_UPDATE_FILES; payload: { files: DraftAttachment[] } }
  | { type: ChatViewAction.DRAFT_REMOVE_SCREENSHOT; payload: { index: number } }
  | { type: ChatViewAction.DRAFT_REMOVE_FILE; payload: { index: number } }
  | { type: ChatViewAction.DRAFT_UPDATE_MESSAGE; payload: { message: string } }
  | {
      type: ChatViewAction.DRAFT_FILE_PROCESSING_START;
      payload: { index: number; isScreenshot: boolean };
    }
  | {
      type: ChatViewAction.DRAFT_FILE_PROCESSING_SUCCESS;
      payload: {
        index: number;
        isScreenshot: boolean;
        s3Key?: string;
        presignedUrl?: string;
        filename?: string;
        // Deprecated: keeping for backward compatibility
        base64: string;
      };
    }
  | {
      type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR;
      payload: { index: number; isScreenshot: boolean; error: string };
    }
  | { type: ChatViewAction.DRAFT_SET_PAGE_CONTEXT; payload: PageContextDraft }
  | { type: ChatViewAction.DRAFT_CLEAR_PAGE_CONTEXT }
  | { type: ChatViewAction.SUBMIT_MESSAGE }
  | { type: ChatViewAction.SET_ERROR; payload: { error: string } }
  | { type: ChatViewAction.SUBMIT_MESSAGE_RESOLVED };

const INPUT_STATE_CHANGE = {
  isLoading: false,
  error: null,
};

const reducer = (state: ChatViewState, action: ChatViewActionType) => {
  switch (action.type) {
    case ChatViewAction.CHAT_CLOSED:
      return {
        ...state,
        sessionId: '',
        draftUserMessage: '',
        draftScreenshots: [],
        draftFiles: [],
        draftPageContext: null,
        isLoading: false,
        sessionPanelVisible: false,
        error: null,
      };
    case ChatViewAction.NEW_CHAT_STARTED:
      return {
        ...state,
        sessionId: '',
        draftUserMessage: '',
        draftScreenshots: [],
        draftFiles: [],
        draftPageContext: null,
        isLoading: false,
        error: null,
      };
    case ChatViewAction.OPEN_CHAT_SESSION:
      return {
        ...state,
        sessionId: action.payload.sessionId,
        targetMessageId: action.payload.messageId ?? null,
        draftUserMessage: '',
        isLoading: false,
        error: null,
      };
    case ChatViewAction.TOGGLE_SESSION_PANEL:
      return {
        ...state,
        sessionPanelVisible: !state.sessionPanelVisible,
      };
    case ChatViewAction.DRAFT_UPDATE_SCREENSHOTS:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        draftScreenshots: action.payload.screenshots,
      };
    case ChatViewAction.DRAFT_UPDATE_FILES:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        draftFiles: action.payload.files,
      };
    case ChatViewAction.DRAFT_REMOVE_SCREENSHOT:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        draftScreenshots: state.draftScreenshots.filter(
          (_, index) => index !== action.payload.index
        ),
      };
    case ChatViewAction.DRAFT_REMOVE_FILE:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        draftFiles: state.draftFiles.filter((_, index) => index !== action.payload.index),
      };
    case ChatViewAction.DRAFT_UPDATE_MESSAGE:
      return { ...state, ...INPUT_STATE_CHANGE, draftUserMessage: action.payload.message };
    case ChatViewAction.DRAFT_FILE_PROCESSING_START:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        ...(action.payload.isScreenshot
          ? {
              draftScreenshots: state.draftScreenshots.map((screenshot, index) =>
                index === action.payload.index
                  ? { ...screenshot, processing: true, processingError: undefined }
                  : screenshot
              ),
            }
          : {
              draftFiles: state.draftFiles.map((file, index) =>
                index === action.payload.index
                  ? { ...file, processing: true, processingError: undefined }
                  : file
              ),
            }),
      };
    case ChatViewAction.DRAFT_FILE_PROCESSING_SUCCESS:
      // Check if the target index still exists
      if (action.payload.isScreenshot) {
        if (action.payload.index >= state.draftScreenshots.length) {
          // Clear processing states for all screenshots since we can't target the specific one
          return {
            ...state,
            ...INPUT_STATE_CHANGE,
            draftScreenshots: state.draftScreenshots.map(screenshot =>
              screenshot.processing ? { ...screenshot, processing: false } : screenshot
            ),
          };
        }
      } else {
        if (action.payload.index >= state.draftFiles.length) {
          // Clear processing states for all files since we can't target the specific one
          return {
            ...state,
            ...INPUT_STATE_CHANGE,
            draftFiles: state.draftFiles.map(file =>
              file.processing ? { ...file, processing: false } : file
            ),
          };
        }
      }

      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        ...(action.payload.isScreenshot
          ? {
              draftScreenshots: state.draftScreenshots.map((screenshot, index) =>
                index === action.payload.index
                  ? {
                      ...screenshot,
                      processing: false,
                      base64: action.payload.base64,
                      ...(action.payload.s3Key && { s3Key: action.payload.s3Key }),
                      ...(action.payload.presignedUrl && {
                        presignedUrl: action.payload.presignedUrl,
                      }),
                      ...(action.payload.filename && { s3Filename: action.payload.filename }),
                    }
                  : screenshot
              ),
            }
          : {
              draftFiles: state.draftFiles.map((file, index) =>
                index === action.payload.index
                  ? {
                      ...file,
                      processing: false,
                      base64: action.payload.base64,
                      ...(action.payload.s3Key && { s3Key: action.payload.s3Key }),
                      ...(action.payload.presignedUrl && {
                        presignedUrl: action.payload.presignedUrl,
                      }),
                      ...(action.payload.filename && { s3Filename: action.payload.filename }),
                    }
                  : file
              ),
            }),
      };
    case ChatViewAction.DRAFT_FILE_PROCESSING_ERROR:
      logger.info(
        `❌ [reducer] DRAFT_FILE_PROCESSING_ERROR for ${action.payload.isScreenshot ? 'screenshot' : 'file'} at index ${action.payload.index}`
      );
      // Check if the target index still exists
      if (action.payload.isScreenshot) {
        if (action.payload.index >= state.draftScreenshots.length) {
          logger.info(
            `⚠️ [reducer] Screenshot index ${action.payload.index} no longer exists, clearing any processing states`
          );
          // Clear processing states for all screenshots since we can't target the specific one
          return {
            ...state,
            ...INPUT_STATE_CHANGE,
            draftScreenshots: state.draftScreenshots.map(screenshot =>
              screenshot.processing ? { ...screenshot, processing: false } : screenshot
            ),
          };
        }
      } else {
        if (action.payload.index >= state.draftFiles.length) {
          logger.info(
            `⚠️ [reducer] File index ${action.payload.index} no longer exists, clearing any processing states`
          );
          // Clear processing states for all files since we can't target the specific one
          return {
            ...state,
            ...INPUT_STATE_CHANGE,
            draftFiles: state.draftFiles.map(file =>
              file.processing ? { ...file, processing: false } : file
            ),
          };
        }
      }

      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        ...(action.payload.isScreenshot
          ? {
              draftScreenshots: state.draftScreenshots.map((screenshot, index) =>
                index === action.payload.index
                  ? { ...screenshot, processing: false, processingError: action.payload.error }
                  : screenshot
              ),
            }
          : {
              draftFiles: state.draftFiles.map((file, index) =>
                index === action.payload.index
                  ? { ...file, processing: false, processingError: action.payload.error }
                  : file
              ),
            }),
      };
    case ChatViewAction.SUBMIT_MESSAGE:
      return {
        ...state,
        ...INPUT_STATE_CHANGE,
        isLoading: true,
        draftUserMessage: '',
        draftScreenshots: [],
        draftFiles: [],
        draftPageContext: null,
        // Preserve sessionId - don't clear it during submission
      };
    case ChatViewAction.SUBMIT_MESSAGE_RESOLVED:
      return { ...state, isLoading: false };
    case ChatViewAction.SET_ERROR:
      return { ...state, ...INPUT_STATE_CHANGE, error: action.payload.error };
    case ChatViewAction.DRAFT_SET_PAGE_CONTEXT:
      return { ...state, draftPageContext: action.payload };
    case ChatViewAction.DRAFT_CLEAR_PAGE_CONTEXT:
      return { ...state, draftPageContext: null };
    default:
      return state;
  }
};

export type UseChatViewStateReturn = {
  state: ChatViewState & {
    currentSession: ReturnType<typeof useCurrentSession>;
    sessions: ReturnType<typeof useS3SessionList>;
    isOnline: boolean;
    isConnecting: boolean;
    draftAttachments: DraftAttachment[];
    draftHasScreenshot: boolean;
    hasPendingImages: boolean;
    hasProcessingImages: boolean;
  };
  actions: {
    closeChat: () => void;
    newChat: () => Promise<void>;
    openChatSession: (sessionId: string, messageId?: string) => void;
    toggleSessionPanel: () => void;
    updateDraftMessage: (message: string) => void;
    updateScreenshots: (screenshots: DraftAttachment[]) => Promise<void>;
    updateAttachments: (files: DraftAttachment[]) => Promise<void>;
    removeScreenshot: (index: number) => void;
    removeAttachment: (index: number) => void;
    setPageContext: (ctx: PageContextDraft) => void;
    clearPageContext: () => void;
    submitMessage: (directMessage?: string) => Promise<void>;
  };
};

export const useChatViewState = (params?: {
  onError: (errorMessage: string) => void;
}): UseChatViewStateReturn => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isOnline, isConnecting } = useNetworkConnectivity();

  // Get personalization settings to send with AI requests
  const activeMode = useActiveMode();
  const userSettings = useUserSettings();

  // AI Skill store — per-session selection
  const aiSkillStore = useAiSkillStore();

  // Use S3 chat integration
  const s3ChatIntegration = useS3ChatIntegration();
  const s3ChatStore = useS3ChatStore();
  const sessions = s3ChatIntegration.sessions;
  const currentSession = s3ChatStore.getSession(state.sessionId);

  // S3 chat actions
  const createSession = s3ChatIntegration.createSession;
  const setCurrentSession = s3ChatIntegration.setCurrentSession;
  const askExpertMutation = useAskExpert();
  const fileUploadMutation = useFileUpload();
  const generateTitleMutation = useGenerateTitleMutation();

  // Track individual screenshot processing to handle race conditions
  const screenShotProcessRefs = React.useRef<Map<number, string>>(new Map());

  // Real S3 file upload handling
  const handleFileAttachment = async (
    attachment: DraftAttachment,
    index: number,
    isScreenshot: boolean
  ) => {
    logger.info(
      `📤 [handleFileAttachment] Starting ${isScreenshot ? 'screenshot' : 'file'} upload:`,
      {
        name: attachment.name,
        contentType: attachment.contentType,
        urlPreview: attachment.url.substring(0, 50) + '...',
        isScreenshot,
      }
    );

    try {
      // Mark as processing
      dispatch({
        type: ChatViewAction.DRAFT_FILE_PROCESSING_START,
        payload: { index, isScreenshot },
      });

      // Convert attachment URL to file data (handle both blob URLs and base64 data URLs)
      let fileBlob: Blob;
      let fileBuffer: ArrayBuffer;

      if (attachment.url.startsWith('data:')) {
        // Handle base64 data URL (common for screenshots)
        logger.info(`📸 [handleFileAttachment] Processing base64 data URL`);

        // Extract base64 data from data URL
        const [mimeTypePart, base64Data] = attachment.url.split(',');
        if (!base64Data) {
          throw new Error('Invalid data URL format');
        }

        // Convert base64 to binary
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        fileBuffer = bytes.buffer;
        fileBlob = new Blob([fileBuffer], { type: attachment.contentType });

        logger.info(`✅ [handleFileAttachment] Converted base64 data URL to blob:`, {
          originalSize: attachment.url.length,
          blobSize: fileBlob.size,
          contentType: attachment.contentType,
        });
      } else {
        // Handle blob URL or regular HTTP(S) URL
        logger.info(`🔗 [handleFileAttachment] Fetching from blob/HTTP URL`);

        const response = await fetch(attachment.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch file data: ${response.statusText}`);
        }

        fileBlob = await response.blob();
        fileBuffer = await fileBlob.arrayBuffer();
      }

      // Prepare form data for S3 upload
      const formData = new FormData();
      formData.append('file', fileBlob, attachment.name);

      const fileMetadata = {
        filename: attachment.name,
        isScreenshot: isScreenshot,
        sessionId: state.sessionId || 'temp-uploads', // Use current session or temp
      };

      formData.append('metadata', JSON.stringify(fileMetadata));

      logger.info(`🔄 [handleFileAttachment] Uploading to S3...`, {
        filename: attachment.name,
        size: fileBuffer.byteLength,
        sessionId: state.sessionId,
        isScreenshot,
      });

      // Upload to S3 using the file upload endpoint
      const uploadResult = await fileUploadMutation.mutateAsync({
        body: formData as any, // FormData will be handled correctly by the upload endpoint
      });

      if (uploadResult.status === 200 && uploadResult.body) {
        const uploadData = uploadResult.body;

        // Mark as successful with S3 data
        dispatch({
          type: ChatViewAction.DRAFT_FILE_PROCESSING_SUCCESS,
          payload: {
            index,
            isScreenshot,
            s3Key: uploadData.s3_key,
            presignedUrl: uploadData.presigned_url,
            filename: uploadData.filename,
            base64: '', // Not needed for S3 uploads
          },
        });

        return {
          s3_key: uploadData.s3_key,
          presigned_url: uploadData.presigned_url,
          filename: uploadData.filename,
        };
      } else {
        throw new Error(`Upload failed with status: ${uploadResult.status}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
      logger.error(`❌ [handleFileAttachment] Upload failed:`, JSON.stringify(error, null, 2));

      // Check if it's a session invalid error and handle logout
      if (error && typeof error === 'object') {
        const errorObj = error as any;
        const isSessionInvalid =
          errorMessage.includes('session_invalid') ||
          errorMessage.includes('Session belongs to different device') ||
          errorMessage.includes('logged in from another device') ||
          (errorObj.status === 401 && errorObj.body?.error === 'session_invalid');

        if (isSessionInvalid) {
          // Handle session invalidation
          import('../../../utils/inline-session-handler')
            .then(({ handleSessionErrorInline }) => {
              handleSessionErrorInline(error);
            })
            .catch(console.error);

          // Don't mark as processing error since the user will be logged out
          return;
        }
      }

      // Mark as failed (only if not a session error)
      dispatch({
        type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR,
        payload: { index, isScreenshot, error: errorMessage },
      });

      throw error;
    }
  };

  const handleTitleGeneration = async (sessionId: string, message: string) => {
    try {
      const titlePromise = await generateTitleMutation.mutateAsync({
        body: { firstMessage: message },
      });
      if (titlePromise.status !== 200 || !titlePromise.body?.title) {
        return;
      }

      const generatedTitle = titlePromise.body.title;
      logger.info('📝 [titleGeneration] Generated title:', {
        sessionId,
        title: generatedTitle,
        wasGenerated: titlePromise.body.generated,
      });

      // Update the session title in the local S3 chat store
      const existingSession = s3ChatStore.getSession(sessionId);
      if (existingSession) {
        const updatedSession = {
          ...existingSession,
          title: generatedTitle,
          updatedAt: new Date().toISOString(),
        };
        s3ChatStore.updateSession(sessionId, updatedSession);
        logger.info('✅ [titleGeneration] Updated session title in store:', {
          sessionId,
          title: generatedTitle,
        });
      } else {
        logger.warn('⚠️ [titleGeneration] Session not found in store for title update:', sessionId);
      }
    } catch (error) {
      logger.error('Failed to generate title:', error);
    }
  };

  const actions = {
    closeChat: () => dispatch({ type: ChatViewAction.CHAT_CLOSED }),
    newChat: async () => {
      // Check if there are images currently processing - if so, don't create new chat
      const currentHasProcessingImages = [...state.draftScreenshots, ...state.draftFiles].some(
        attachment => attachment.processing === true
      );
      if (currentHasProcessingImages) {
        logger.info('⚠️ [newChat] Skipping new chat creation - images are currently processing');
        return;
      }

      logger.info('🆕 [newChat] Starting new chat (session will be created on first message)');

      // Start a new chat without creating a session (session created on first message)
      dispatch({ type: ChatViewAction.NEW_CHAT_STARTED });

      // New chats always start with no specialist selected. The user picks one
      // explicitly if they want it — we don't carry the last-used skill forward.
      aiSkillStore.clearSkill('new');
    },
    openChatSession: async (sessionId: string, messageId?: string) => {
      dispatch({
        type: ChatViewAction.OPEN_CHAT_SESSION,
        payload: messageId ? { sessionId, messageId } : { sessionId },
      });

      // Load messages for this session from S3 if they haven't been loaded yet
      const existingSession = s3ChatStore.getSession(sessionId);
      if (
        existingSession &&
        existingSession.messages.length === 0 &&
        existingSession.messageCount > 0
      ) {
        logger.info('📂 [openChatSession] Loading messages for session:', sessionId);

        // Mark session as loading
        s3ChatStore.updateSession(sessionId, { isLoading: true });

        try {
          // Get access token like the API hooks do
          let accessToken: string | null = null;
          try {
            const { getValidAccessToken } = await import('../../../auth/token-manager');
            accessToken = await getValidAccessToken();
          } catch (tokenError) {
            logger.error('Failed to get access token:', tokenError);
          }

          const authHeader = accessToken ? `Bearer ${accessToken}` : `Bearer dev-public-key`;

          // Import getDeviceId for consistent device identification
          const { getDeviceId } = await import('../../../api/client');

          const response = await fetch(`${API_BASE_URL}/chat-history/${sessionId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
              'x-app-source': 'enter-ctrl-web', // Fixed: was 'enter-ctrl-desktop'
              'x-device-id': getDeviceId(), // IMPORTANT: Include device ID for session validation
            },
          });

          if (response.ok) {
            const result = await response.json();
            const chatSession = result.session;

            if (chatSession && chatSession.messages) {
              // Convert S3 messages to local format and update the session in store
              const localMessages = chatSession.messages.map((msg: any) => ({
                id: msg.id || randomUUID(),
                content: msg.content,
                sender: msg.sender,
                timestamp: msg.created_at,
                // Support both legacy string and new AttachmentMetadata
                attachedFiles: (msg.attached_files || [])
                  .map((att: any) => (typeof att === 'string' ? att : att?.url || ''))
                  .filter((u: string) => typeof u === 'string' && u.length > 0),
                status: 'sent',
                // Restore page context chip from S3 metadata if present
                ...(msg.page_context ? { pageContext: msg.page_context } : {}),
              }));

              // Update the session with loaded messages and clear loading state
              const updatedSession = {
                ...existingSession,
                messages: localMessages,
                updatedAt: chatSession.metadata.updated_at,
                messageCount: chatSession.metadata.message_count,
                isLoading: false,
                // Restore compaction state from S3 metadata if present
                ...(chatSession.metadata.compaction_token !== undefined && {
                  compactionToken: chatSession.metadata.compaction_token,
                  compactionMessageIndex: chatSession.metadata.compaction_message_index,
                }),
              };

              // Update the session in the store
              s3ChatStore.updateSession(sessionId, updatedSession);

              // Restore active AI skill from S3 metadata if not already set client-side
              if (chatSession.metadata.ai_skill_id !== undefined) {
                aiSkillStore.hydrateFromSession(sessionId, chatSession.metadata.ai_skill_id);
              }

              logger.info('✅ [openChatSession] Successfully loaded messages:', {
                sessionId,
                messageCount: localMessages.length,
                aiSkillId: chatSession.metadata.ai_skill_id,
              });
            }
          } else {
            logger.error('Failed to fetch chat history:', response.status, response.statusText);
            // Mark loading as complete even on error
            s3ChatStore.updateSession(sessionId, { isLoading: false });
          }
        } catch (error) {
          logger.error('Failed to load session messages:', error);
          // Mark loading as complete even on error
          s3ChatStore.updateSession(sessionId, { isLoading: false });
        }
      }
    },
    toggleSessionPanel: () => dispatch({ type: ChatViewAction.TOGGLE_SESSION_PANEL }),
    updateDraftMessage: (message: string) =>
      dispatch({ type: ChatViewAction.DRAFT_UPDATE_MESSAGE, payload: { message } }),
    updateScreenshots: async (screenshots: DraftAttachment[]) => {
      // Compare with current state to find only newly added screenshots
      const currentScreenshots = state.draftScreenshots;
      const newScreenshots: Array<{ screenshot: DraftAttachment; index: number }> = [];

      // Find screenshots that are new (not in the current state)
      for (let i = 0; i < screenshots.length; i++) {
        const screenshot = screenshots[i];
        if (screenshot && !screenshot.s3Key && !screenshot.processing) {
          // Check if this screenshot is new by comparing with current state
          const existsInCurrent = currentScreenshots.some(
            current => current.name === screenshot.name && current.url === screenshot.url
          );

          if (!existsInCurrent) {
            newScreenshots.push({ screenshot, index: i });
          }
        }
      }

      dispatch({ type: ChatViewAction.DRAFT_UPDATE_SCREENSHOTS, payload: { screenshots } });

      // Track screenshot attachment
      if (newScreenshots.length > 0) {
        import('../../../services/analytics')
          .then(({ mixpanel }) => {
            mixpanel.track('Attachment Added', {
              type: 'screenshot',
              count: newScreenshots.length,
            });
          })
          .catch(error => {
            logger.warn('⚠️ Failed to track analytics:', error);
          });
      }

      // Process only the new screenshots using their correct indices
      for (const { screenshot, index } of newScreenshots) {
        // Set individual process ID to handle race conditions for this specific screenshot
        const processId = `${screenshot.name}-${Date.now()}-${index}`;
        screenShotProcessRefs.current.set(index, processId);

        try {
          await handleFileAttachment(screenshot, index, true);

          // Check if this process is still current for this specific screenshot
          if (screenShotProcessRefs.current.get(index) !== processId) {
            // Clear processing state even if superseded
            dispatch({
              type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR,
              payload: { index, isScreenshot: true, error: 'Processing superseded' },
            });
            return;
          }

          // Clear the process ref for this screenshot on success
          screenShotProcessRefs.current.delete(index);
        } catch (error) {
          // Check if the processing was cancelled before dispatching error
          if (!screenShotProcessRefs.current.has(index)) {
            // Processing was cancelled - don't dispatch error, just clean up
            logger.info(
              `⚠️ [updateScreenshots] Processing was cancelled for index ${index}, skipping error dispatch`
            );
            return;
          }

          // Always clear processing state on error, regardless of race condition
          const errorMessage = error instanceof Error ? error.message : 'Processing failed';
          dispatch({
            type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR,
            payload: { index, isScreenshot: true, error: errorMessage },
          });

          // Check if this process is still current before removing screenshot
          if (screenShotProcessRefs.current.get(index) !== processId) {
            screenShotProcessRefs.current.delete(index);
            return;
          }
          // For screenshots: remove from state on error (silent failure)
          screenShotProcessRefs.current.delete(index);
          dispatch({
            type: ChatViewAction.DRAFT_REMOVE_SCREENSHOT,
            payload: { index },
          });
        }
      }
    },
    updateAttachments: async (files: DraftAttachment[]) => {
      dispatch({ type: ChatViewAction.DRAFT_UPDATE_FILES, payload: { files } });

      // Track file attachment
      const newFiles = files.filter(f => !f.s3Key && !f.processing);
      if (newFiles.length > 0) {
        import('../../../services/analytics')
          .then(({ mixpanel }) => {
            mixpanel.track('Attachment Added', {
              type: 'file',
              count: newFiles.length,
              fileTypes: [...new Set(newFiles.map(f => f.contentType || 'unknown'))],
            });
          })
          .catch(error => {
            logger.warn('⚠️ Failed to track analytics:', error);
          });
      }

      // Process new files immediately with optimistic loading states
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file && !file.s3Key && !file.processing) {
          try {
            await handleFileAttachment(file, i, false);
          } catch {
            // For files: show error state but keep in UI for user to retry/remove
            // The compressAndUploadToS3 function will dispatch the error state
          }
        }
      }
    },
    removeScreenshot: (index: number) => {
      logger.info(`🗁️ [removeScreenshot] Removing screenshot at index ${index}`);
      logger.info(
        `🗁️ [removeScreenshot] Screenshots before removal:`,
        state.draftScreenshots.map((s, i) => ({ index: i, name: s.name, processing: s.processing }))
      );

      // Cancel only the specific screenshot processing being removed
      const wasTracking = screenShotProcessRefs.current.has(index);
      screenShotProcessRefs.current.delete(index);
      logger.info(
        `🗁️ [removeScreenshot] Processing tracking removed for index ${index}, was tracking: ${wasTracking}`
      );

      // Get the attachment being removed to clean up bucket store if needed
      const screenshot = state.draftScreenshots[index];

      // If the screenshot is currently processing, clear that state first
      if (screenshot?.processing) {
        dispatch({
          type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR,
          payload: { index, isScreenshot: true, error: 'Processing cancelled by user' },
        });
      }

      // Note: We no longer use OpenAI file IDs, so no need to clean up bucket store with file IDs
      dispatch({ type: ChatViewAction.DRAFT_REMOVE_SCREENSHOT, payload: { index } });

      // Don't remap indices for ongoing processing - let them complete with their original indices
      // The reducer will handle invalid indices gracefully
    },
    removeAttachment: (index: number) => {
      // Get the file being removed
      const file = state.draftFiles[index];

      // If the file is currently processing, clear that state first
      if (file?.processing) {
        dispatch({
          type: ChatViewAction.DRAFT_FILE_PROCESSING_ERROR,
          payload: { index, isScreenshot: false, error: 'Processing cancelled by user' },
        });
      }

      // Note: We no longer use OpenAI file IDs, so no need to clean up bucket store with file IDs
      dispatch({ type: ChatViewAction.DRAFT_REMOVE_FILE, payload: { index } });
    },

    setPageContext: (ctx: PageContextDraft) => {
      dispatch({ type: ChatViewAction.DRAFT_SET_PAGE_CONTEXT, payload: ctx });
    },

    clearPageContext: () => {
      dispatch({ type: ChatViewAction.DRAFT_CLEAR_PAGE_CONTEXT });
    },

    submitMessage: async (directMessage?: string) => {
      // Use directMessage if provided (for starter prompts), otherwise use state
      const userInput = directMessage || state.draftUserMessage;

      // Validate userInput is not empty
      if (!userInput || !userInput.trim()) {
        return;
      }

      let sessionId = state.sessionId;

      // Check if there are any files still processing
      const processingAttachments = [...state.draftScreenshots, ...state.draftFiles].filter(
        attachment => attachment.processing
      );

      if (processingAttachments.length > 0) {
        const errorMessage = `Please wait for ${processingAttachments.length} file(s) to finish uploading before sending the message.`;
        logger.warn('⚠️ [submitMessage] Blocked - files still processing:', {
          processingCount: processingAttachments.length,
          processingFiles: processingAttachments.map(a => a.name),
        });

        // Show toast error for better user feedback
        try {
          const { toast } = await import('../../../utils/toast');
          toast.error('Upload in Progress', errorMessage);
        } catch (toastError) {
          logger.warn('Failed to show error toast:', toastError);
        }

        return; // Don't proceed with submission
      }

      // Check if there are any files with upload errors
      const failedAttachments = [...state.draftScreenshots, ...state.draftFiles].filter(
        attachment => attachment.processingError
      );

      if (failedAttachments.length > 0) {
        const errorMessage = `Please remove or retry uploading the ${failedAttachments.length} failed file(s) before sending the message.`;
        logger.warn('⚠️ [submitMessage] Blocked - files have upload errors:', {
          failedCount: failedAttachments.length,
          failedFiles: failedAttachments.map(a => ({ name: a.name, error: a.processingError })),
        });

        // Show toast error for better user feedback
        try {
          const { toast } = await import('../../../utils/toast');
          toast.error('Upload Failed', errorMessage);
        } catch (toastError) {
          logger.warn('Failed to show error toast:', toastError);
        }

        return; // Don't proceed with submission
      }
      // Create session on first message if it doesn't exist
      if (!sessionId) {
        logger.info('🆕 [submitMessage] Creating new session for first message');
        sessionId = (await createSession()) || randomUUID();
        if (sessionId) {
          // Add the session to the S3 store so we can add messages to it
          const newSession = {
            id: sessionId,
            title: 'New Chat', // Will be updated by AI title generation below
            messages: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            messageCount: 0,
            isArchived: false,
          };
          s3ChatStore.addSession(newSession);
          setCurrentSession(sessionId);
          dispatch({ type: ChatViewAction.OPEN_CHAT_SESSION, payload: { sessionId } });

          // Migrate the 'new' skill sentinel to the real session ID
          const pendingSkillId = aiSkillStore.selectedSkillIdBySession['new'];
          if (pendingSkillId !== undefined) {
            aiSkillStore.selectSkill(sessionId, pendingSkillId);
            aiSkillStore.clearSkill('new');
          }

          // Track session created event
          import('../../../services/analytics')
            .then(({ mixpanel }) => {
              mixpanel.track('Chat Session Created', {
                source: 'first_message',
              });
            })
            .catch(error => {
              logger.warn('⚠️ [submitMessage] Failed to track analytics:', error);
            });

          logger.info('✅ [submitMessage] Created new session:', sessionId);
        } else {
          throw new Error('Failed to create new chat session');
        }
      }
      // Handle title generation asynchronously for new sessions or sessions with default titles
      const currentSessionData = s3ChatStore.getSession(sessionId);
      const shouldGenerateTitle =
        !currentSessionData ||
        currentSessionData.title === 'New Chat' ||
        currentSessionData.title.startsWith('Chat ') ||
        currentSessionData.messageCount === 0;

      if (shouldGenerateTitle) {
        logger.info('🏷️ [submitMessage] Generating AI title for session:', sessionId);
        handleTitleGeneration(sessionId, userInput).catch(error => {
          logger.error('Error generating title:', error);
        });
      } else {
        logger.info(
          '🔄 [submitMessage] Skipping title generation for existing session with custom title'
        );
      }

      // Collect S3 file URLs separated by type BEFORE clearing state
      logger.info('🔍 [submitMessage] Draft state before processing:', {
        screenshotsCount: state.draftScreenshots.length,
        filesCount: state.draftFiles.length,
        screenshots: state.draftScreenshots.map((s, i) => ({
          index: i,
          name: s.name,
          hasS3Key: !!s.s3Key,
          hasPresignedUrl: !!s.presignedUrl,
          processing: s.processing,
          processingError: s.processingError,
        })),
        files: state.draftFiles.map((f, i) => ({
          index: i,
          name: f.name,
          hasS3Key: !!f.s3Key,
          hasPresignedUrl: !!f.presignedUrl,
          processing: f.processing,
          processingError: f.processingError,
        })),
      });

      const screenshots = state.draftScreenshots
        .filter(
          (screenshot): screenshot is DraftAttachment & { s3Key: string; presignedUrl: string } => {
            const hasValidData = !!screenshot.s3Key && !!screenshot.presignedUrl;
            if (!hasValidData) {
              logger.warn('⚠️ [submitMessage] Skipping screenshot without valid S3 data:', {
                name: screenshot.name,
                hasS3Key: !!screenshot.s3Key,
                hasPresignedUrl: !!screenshot.presignedUrl,
                processing: screenshot.processing,
                processingError: screenshot.processingError,
              });
            }
            return hasValidData;
          }
        )
        .map(screenshot => ({
          s3_key: screenshot.s3Key,
          url: screenshot.presignedUrl,
          mimeType: screenshot.contentType,
          filename: screenshot.name,
          isScreenshot: true,
        }));

      const attachments = state.draftFiles
        .filter((file): file is DraftAttachment & { s3Key: string; presignedUrl: string } => {
          const hasValidData = !!file.s3Key && !!file.presignedUrl;
          if (!hasValidData) {
            logger.warn('⚠️ [submitMessage] Skipping file without valid S3 data:', {
              name: file.name,
              hasS3Key: !!file.s3Key,
              hasPresignedUrl: !!file.presignedUrl,
              processing: file.processing,
              processingError: file.processingError,
            });
          }
          return hasValidData;
        })
        .map(file => ({
          s3_key: file.s3Key,
          url: file.presignedUrl,
          mimeType: file.contentType,
          filename: file.name,
          isScreenshot: false,
        }));

      logger.info('✅ [submitMessage] Processed attachments for sending:', {
        screenshotCount: screenshots.length,
        attachmentCount: attachments.length,
        totalAttachments: screenshots.length + attachments.length,
      });

      // Session should already be created above

      // Track message sent event
      import('../../../services/analytics')
        .then(({ mixpanel }) => {
          mixpanel.track('Chat Message Sent', {
            hasScreenshots: screenshots.length > 0,
            hasAttachments: attachments.length > 0,
            screenshotCount: screenshots.length,
            attachmentCount: attachments.length,
            totalAttachments: screenshots.length + attachments.length,
            messageLength: userInput.length, // Use userInput instead of state.draftUserMessage
            activeMode,
            aiSkillId: sessionId ? (aiSkillStore.selectedSkillIdBySession[sessionId] ?? null) : null,
          });
        })
        .catch(error => {
          logger.warn('⚠️ Failed to track analytics:', error);
        });

      // Clear draft state immediately - ONLY ONCE
      dispatch({ type: ChatViewAction.SUBMIT_MESSAGE });

      let assistantMessageId = '';
      let userMessageId = '';

      try {
        // Get chat history for context BEFORE adding current message
        const currentSessionData = s3ChatStore.getSession(sessionId);
        const chatHistory: {
          role: 'user' | 'assistant';
          content: string;
        }[] =
          currentSessionData?.messages.map(msg => {
            // IMPORTANT: For messages with attachments, we DON'T re-send them in history
            // because they were already processed by OpenAI when originally sent.
            // Re-sending causes the backend to reprocess them, and without proper mimeType
            // metadata, CSV/Excel files get treated as images, causing OpenAI errors.
            // The extracted text from documents is already embedded in msg.content.
            return {
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.content,
            };
          }) || [];

        // Add user message to the chat store first (optimistic)
        // For immediate display, we need the presigned URLs, not just S3 keys
        const allAttachments = [...screenshots, ...attachments];
        const attachedFileUrls = allAttachments.map(att => att.url || '');

        // Create attachment metadata with token counts for accurate context tracking
        // Use original draft attachments to preserve token estimates and file sizes
        const allDraftAttachments = [...state.draftScreenshots, ...state.draftFiles];
        const attachmentMetadata = allDraftAttachments
          .filter((att): att is DraftAttachment & { s3Key: string } => !!att.s3Key) // Only include successfully uploaded files
          .map(att => ({
            s3Key: att.s3Key,
            fileName: att.name,
            fileSizeMB: att.fileSizeMB || 0,
            estimatedTokens: att.estimatedTokens || 0,
            contentType: att.contentType,
          }));

        userMessageId = s3ChatStore.addOptimisticMessage(sessionId, {
          content: userInput,
          sender: 'user',
          timestamp: new Date().toISOString(),
          attachedFiles: attachedFileUrls, // Store presigned URLs for immediate display
          attachmentMetadata, // Store metadata including token counts
          status: 'sending',
          ...(state.draftPageContext
            ? {
                pageContext: {
                  title: state.draftPageContext.title,
                  url: state.draftPageContext.url,
                  charCount: state.draftPageContext.charCount,
                  wasTruncated: state.draftPageContext.wasTruncated,
                },
              }
            : {}),
        });

        logger.info('📎 [submitMessage] Storing user message with attachments:', {
          userMessageId,
          attachmentCount: allAttachments.length,
          attachmentDetails: allAttachments.map(att => ({
            filename: att.filename,
            s3Key: att.s3_key,
            hasUrl: !!att.url,
            urlPreview: att.url?.substring(0, 50) + '...',
          })),
        });

        // Add placeholder assistant message for streaming updates
        assistantMessageId = s3ChatStore.addOptimisticMessage(sessionId, {
          content: '',
          sender: 'ai',
          timestamp: new Date().toISOString(),
          attachedFiles: [],
          status: 'processing', // Start with 'processing' to show loading dots immediately
        });

        logger.info('📝 [submitMessage] Added optimistic messages to store:', {
          userMessageId,
          assistantMessageId,
          sessionId,
        });

        // Use the enhanced input as-is for now
        // PDF processing and S3 file handling will be done server-side
        const enhancedUserInput = userInput;

        // Get selected MCP account ID from localStorage
        let selectedMcpAccountId: string | undefined;
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            selectedMcpAccountId = localStorage.getItem('selectedMcpAccountId') || undefined;
          }
        } catch (error) {
          logger.error('Failed to read selectedMcpAccountId from localStorage:', error);
        }

        // Send JSON request to AI service with S3 URLs
        // IMPORTANT: Backend expects BOTH attachmentUrls (URLs only) AND attachments (full metadata)
        // to properly match mimeType info with URLs (see operations.ts line 280)
        // The backend type AskExpertWithImages extends AskExpertInput with these URL fields
        // If the session has a compaction token, pass it so the server can skip
        // replaying fully-compacted history and use the encrypted token instead.
        const sessionCompactionData = currentSessionData
          ? {
              compactionToken: currentSessionData.compactionToken,
              compactionMessageIndex: currentSessionData.compactionMessageIndex,
            }
          : {};

        const askExpertInput: AskExpertInput & {
          screenshotUrls?: string[];
          attachmentUrls?: string[];
        } = {
          userInput: enhancedUserInput,
          chatHistory,
          screenshots, // Pass S3 URL screenshot data
          screenshotUrls: screenshots.map(s => s.url), // Also pass URL strings for backend compat
          attachments, // Pass S3 URL attachment data with metadata
          attachmentUrls: attachments.map(a => a.url), // Also pass URL strings for backend compat
          sessionId, // Include session ID for S3 auto-save
          selectedMcpAccountId, // Include selected MCP account ID if available
          // Include captured page content as additional context for the AI
          ...(state.draftPageContext
            ? {
                additionalContext: [
                  '=== Captured Web Page ===',
                  state.draftPageContext.content,
                  '=== End of Captured Web Page ===',
                ].join('\n'),
                // Metadata stored with the message in S3 for chip display (not sent to AI)
                pageContextMeta: {
                  title: state.draftPageContext.title,
                  url: state.draftPageContext.url,
                  charCount: state.draftPageContext.charCount,
                  wasTruncated: state.draftPageContext.wasTruncated,
                },
              }
            : {}),
          personalization: {
            // Include user personalization for AI context
            activeMode,
            userSettings,
          },
          ...sessionCompactionData,
          // Include active AI skill (if any) for prompt injection
          ...(sessionId && aiSkillStore.selectedSkillIdBySession[sessionId] != null
            ? { aiSkillId: aiSkillStore.selectedSkillIdBySession[sessionId]! }
            : {}),
        };

        if (state.draftPageContext) {
          logger.info('🌐 [submitMessage] Page context included:', {
            title: state.draftPageContext.title,
            charCount: state.draftPageContext.charCount,
            wasTruncated: state.draftPageContext.wasTruncated,
          });
        }

        logger.info('✨ [submitMessage] Including personalization:', {
          activeMode,
          hasUserSettings: Object.keys(userSettings).length > 0,
          userSettingsKeys: Object.keys(userSettings),
        });

        let result;
        try {
          // Wrap API call with network retry logic (max 3 attempts, exponential backoff)
          result = await withRetry(
            () =>
              askExpertMutation.mutateAsync({
                body: askExpertInput,
                headers: {
                  'x-stream': 'true',
                },
              }),
            {
              maxRetries: 3,
              initialDelayMs: 1000,
              maxDelayMs: 10000,
              onRetry: (attempt: number, delay: number) => {
                logger.warn(`🔄 [submitMessage] Retry attempt ${attempt + 1} after ${delay}ms`);
              },
            }
          );
          logger.info('📡 [submitMessage] API call succeeded:', {
            status: result.status,
            hasBody: !!result.body,
            bodyType: typeof result.body,
          });
        } catch (mutationError) {
          logger.error('❌ [submitMessage] Mutation error (after retries):', {
            error: mutationError,
            errorType: typeof mutationError,
            errorMessage:
              mutationError instanceof Error ? mutationError.message : String(mutationError),
          });
          throw mutationError;
        }

        if (result.status !== 200) {
          // Extract meaningful error message from response
          let errorMessage = `AI API Error: ${result.status}`;

          logger.info('❌ [submitMessage] Non-200 status, extracting error:', {
            status: result.status,
            body: result.body,
            bodyType: typeof result.body,
          });

          if (result.body && typeof result.body === 'object') {
            if ('error' in result.body && typeof result.body.error === 'string') {
              errorMessage = result.body.error;
            } else if ('message' in result.body && typeof result.body.message === 'string') {
              errorMessage = result.body.message;
            } else if ('details' in result.body && typeof result.body.details === 'string') {
              errorMessage = result.body.details;
            }
          }

          logger.info('❌ [submitMessage] Extracted error message:', errorMessage);

          // Provide better error messaging for PDF/document limitations
          if (
            errorMessage.includes('Expected file type to be a supported format') &&
            errorMessage.includes('.pdf')
          ) {
            errorMessage =
              'PDF files are not yet supported for AI analysis. Please use images (JPEG, PNG, WebP, GIF) for now. PDF support is coming soon!';
          } else if (
            errorMessage.includes('supported format') &&
            !errorMessage.includes('image/')
          ) {
            errorMessage =
              'Only image files (JPEG, PNG, WebP, GIF) are currently supported for AI analysis. Document support is coming soon!';
          }

          throw new Error(errorMessage);
        }

        if (!result.body) {
          throw new Error('No response body received from AI service');
        }

        const stream = result.body;
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let buffer = ''; // Buffer for incomplete SSE events

        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (streamError) {
            // Stream error (e.g., connection aborted)
            logger.error('❌ [submitMessage] Stream read error:', streamError);

            // Extract error message from stream error
            const streamErrorMessage =
              streamError instanceof Error ? streamError.message : String(streamError);

            throw new Error(streamErrorMessage);
          }

          const { done, value } = readResult;

          if (done) {
            // Mark both messages as sent
            s3ChatStore.updateMessageStatus(sessionId, userMessageId, 'sent');
            s3ChatStore.updateMessageStatus(sessionId, assistantMessageId, 'sent');

            logger.info('✅ [submitMessage] Final response received:', {
              sessionId,
              messageId: assistantMessageId,
              responseLength: fullResponse.length,
            });

            // Track message received event
            import('../../../services/analytics')
              .then(({ mixpanel }) => {
                mixpanel.track('Chat Message Received', {
                  responseLength: fullResponse.length,
                  activeMode,
                });
              })
              .catch(error => {
                logger.warn('⚠️ Failed to track analytics:', error);
              });

            break;
          }

          const textChunk = decoder.decode(value, { stream: true });
          buffer += textChunk;

          // Check if this chunk contains SSE event markers
          if (buffer.includes('event:')) {
            // Parse SSE events
            const lines = buffer.split('\n');
            buffer = ''; // Clear buffer after processing events

            let currentEvent = '';
            let currentData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEvent = line.substring(7).trim();
              } else if (line.startsWith('data: ')) {
                currentData = line.substring(6).trim();
              } else if (line === '' && currentEvent && currentData) {
                // End of event
                if (currentEvent === 'status') {
                  try {
                    const statusData = JSON.parse(currentData);
                    // Only update with status if there's no content yet
                    if (!fullResponse) {
                      s3ChatStore.updateMessageContent(
                        sessionId,
                        assistantMessageId,
                        `_${statusData.status}_`
                      );
                    }
                    logger.info('🔍 [submitMessage] Status update:', statusData.status);
                  } catch (e) {
                    logger.warn('Failed to parse status event:', currentData);
                  }
                } else if (currentEvent === 'error') {
                  // Error event from backend
                  try {
                    const errorData = JSON.parse(currentData);
                    logger.info(
                      '❌ [submitMessage] Received error event from backend:',
                      JSON.stringify(errorData, null, 2)
                    );
                    throw new Error(errorData.error || 'Stream error');
                  } catch (parseError) {
                    if (parseError instanceof SyntaxError) {
                      logger.warn('Failed to parse error event:', currentData);
                      throw new Error(currentData);
                    }
                    throw parseError;
                  }
                }
                currentEvent = '';
                currentData = '';
              } else if (!line.startsWith('event:') && !line.startsWith('data:') && line.trim()) {
                // Keep non-event lines in buffer
                buffer += line + '\n';
              }
            }
          } else {
            // No SSE events, treat as regular text content
            fullResponse += buffer;
            buffer = '';

            // Update status to 'streaming' on first content chunk
            if (fullResponse.length === textChunk.length) {
              s3ChatStore.updateMessageStatus(sessionId, assistantMessageId, 'streaming');
            }

            // Update the assistant message with streaming content
            s3ChatStore.updateMessageContent(sessionId, assistantMessageId, fullResponse);
          }
        }
      } catch (error) {
        logger.error('❌ [submitMessage] Error caught:', error);

        // Extract meaningful error message
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error === 'object') {
          // Handle mutation errors from ts-rest
          if ('status' in error) {
            errorMessage = `Request failed with status ${(error as any).status}`;
            if ('body' in error && (error as any).body && typeof (error as any).body === 'object') {
              if ('error' in (error as any).body && typeof (error as any).body.error === 'string') {
                errorMessage = (error as any).body.error;
              } else if (
                'message' in (error as any).body &&
                typeof (error as any).body.message === 'string'
              ) {
                errorMessage = (error as any).body.message;
              }
            }
          }
        }

        const err = new Error(errorMessage);

        // Rollback assistant placeholder, but KEEP the user message so the UI can
        // show an inline retry button on it instead of losing the user's input.
        if (userMessageId) {
          s3ChatStore.updateMessageStatus(sessionId, userMessageId, 'error');
        }
        if (assistantMessageId) {
          s3ChatStore.removeOptimisticMessage(sessionId, assistantMessageId);
        }
        logger.warn('⏪ [submitMessage] Rolled back assistant placeholder; user message kept as errored:', {
          sessionId,
          userMessageId,
          assistantMessageId,
        });

        // Show toast error for better user feedback
        try {
          const { toast } = await import('../../../utils/toast');
          toast.error('Chat Error', errorMessage);
        } catch (toastError) {
          logger.warn('Failed to show error toast:', toastError);
        }

        params?.onError?.(err.message);
        throw err;
      } finally {
        dispatch({ type: ChatViewAction.SUBMIT_MESSAGE_RESOLVED });
      }
    },
  };

  // Combine all draft attachments for UI display
  const draftAttachments = [...state.draftScreenshots, ...state.draftFiles];
  const draftHasScreenshot = state.draftScreenshots.length > 0;

  // Helper function to detect if there are pending images (unsent)
  const hasPendingImages = draftAttachments.length > 0;

  // Helper function to detect if any images are currently processing
  const hasProcessingImages = draftAttachments.some(attachment => attachment.processing === true);

  return {
    state: {
      ...state,
      currentSession,
      sessions,
      isOnline,
      isConnecting,
      draftAttachments,
      draftHasScreenshot,
      hasPendingImages,
      hasProcessingImages,
    },
    actions,
  };
};
